/** Shared narration TTS provider / voice settings (create flow + scene grid editor). */

export const NARRATION_TTS_PROVIDER_OPTIONS = [
  { value: 'auto', label: 'Auto' },
  { value: 'minimax', label: 'MiniMax' },
  { value: 'elevenlabs', label: 'ElevenLabs' },
  { value: 'murf', label: 'Murf' },
];

export const NARRATION_VOICE_STYLE_OPTIONS = [
  { value: 'storyteller', label: 'Storyteller' },
  { value: 'female', label: 'Female' },
  { value: 'male', label: 'Male' },
  { value: 'child', label: 'Child' },
  { value: 'newscaster', label: 'Newscaster' },
];

export const NARRATION_AUDIO_LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'hi', label: 'Hindi' },
  { value: 'kn', label: 'Kannada' },
  { value: 'mr', label: 'Marathi' },
  { value: 'ta', label: 'Tamil' },
  { value: 'pa', label: 'Punjabi' },
  { value: 'bho', label: 'Bhojpuri' },
];

/** Human-readable label for script / TTS language codes. */
export function narrationScriptLanguageLabel(code) {
  const key = String(code || 'en').trim().toLowerCase();
  const match = NARRATION_AUDIO_LANGUAGE_OPTIONS.find((o) => o.value === key);
  return match?.label || key || 'English';
}

export const NARRATION_TTS_PROVIDER_LABELS = {
  auto: 'Auto',
  minimax: 'MiniMax',
  elevenlabs: 'ElevenLabs',
  murf: 'Murf',
};

export const DEFAULT_NARRATION_AUDIO_SETTINGS = {
  voice_style: 'storyteller',
  tts_provider: 'auto',
  language: 'en',
  narrator_elevenlabs_voice_id: '',
  narrator_murf_voice_id: '',
  narrator_murf_locale: '',
  narrator_minimax_voice_id: '',
};

export function narrationAudioSettingsFromInput(input) {
  const src = input && typeof input === 'object' ? input : {};
  return {
    voice_style: String(src.voice_style || DEFAULT_NARRATION_AUDIO_SETTINGS.voice_style),
    tts_provider: String(src.tts_provider || DEFAULT_NARRATION_AUDIO_SETTINGS.tts_provider),
    language: String(src.language || DEFAULT_NARRATION_AUDIO_SETTINGS.language),
    narrator_elevenlabs_voice_id: String(src.narrator_elevenlabs_voice_id || ''),
    narrator_murf_voice_id: String(src.narrator_murf_voice_id || ''),
    narrator_murf_locale: String(src.narrator_murf_locale || ''),
    narrator_minimax_voice_id: String(src.narrator_minimax_voice_id || ''),
  };
}

export function narrationSelectedVoiceLabel(settings) {
  const p = settings?.tts_provider || 'auto';
  if (p === 'elevenlabs') {
    return settings?.narrator_elevenlabs_voice_id?.trim() || 'No voice selected';
  }
  if (p === 'murf') {
    const id = settings?.narrator_murf_voice_id?.trim();
    const loc = settings?.narrator_murf_locale?.trim();
    if (!id) return 'No voice selected';
    return loc ? `${id} (${loc})` : id;
  }
  if (p === 'minimax') {
    return settings?.narrator_minimax_voice_id?.trim() || 'No voice selected';
  }
  return 'Preset from voice style';
}

/** Payload for segment regenerate-audio / adapt-script (snake_case API fields). */
export function buildNarrationAudioApiPayload(settings, { prompt } = {}) {
  const s = narrationAudioSettingsFromInput(settings);
  const payload = {
    voice_style: s.voice_style,
    language: s.language,
    tts_provider: s.tts_provider,
  };
  if (prompt != null && String(prompt).trim()) {
    payload.prompt = String(prompt).trim();
  }
  if (s.narrator_minimax_voice_id.trim()) {
    payload.narrator_minimax_voice_id = s.narrator_minimax_voice_id.trim();
  }
  if (s.narrator_elevenlabs_voice_id.trim()) {
    payload.narrator_elevenlabs_voice_id = s.narrator_elevenlabs_voice_id.trim();
  }
  if (s.narrator_murf_voice_id.trim()) {
    payload.narrator_murf_voice_id = s.narrator_murf_voice_id.trim();
  }
  if (s.narrator_murf_locale.trim()) {
    payload.narrator_murf_locale = s.narrator_murf_locale.trim();
  }
  return payload;
}
